<style scoped>
.exception-note {
  white-space: normal;
  word-break: break-word;
}
</style>
<template>
  <div class="col-xl-12 mb-5 mb-xl-10">
    <div class="card card-flush h-xl-100">
      <div class="card-header pt-4">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label fw-bolder fs-3 mb-1">Cancellation Exceptions</span>
          <span class="text-muted mt-1 fw-bold fs-7">
            Orders that need someone to look at them
          </span>
        </h3>
        <div class="card-toolbar">
          <div class="row justify-content-end align-items-center">
            <div class="col-auto">
              <el-select size="large" v-model="filterData.category" placeholder="All categories" @change="applyFilter">
                <el-option label="All categories" value=""></el-option>
                <el-option
                  v-for="option in categoryOptions"
                  :key="option.value"
                  :label="option.label + (counts[option.value] ? ' (' + counts[option.value] + ')' : '')"
                  :value="option.value"
                ></el-option>
              </el-select>
            </div>
            <div class="col-auto">
              <el-select size="large" v-model="filterData.days" placeholder="Last 30 days" @change="getExceptions">
                <el-option v-for="option in dayOptions" :key="option" :label="'Last ' + option + ' days'" :value="option"></el-option>
              </el-select>
            </div>
            <div class="col-auto">
              <el-tooltip class="box-item" effect="dark" content="Refresh" placement="top">
                <button type="button" @click="getExceptions" class="btn btn-icon btn-light btn-sm">
                  <i class="bi bi-arrow-counterclockwise"></i>
                </button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body pt-2">
        <div v-if="permissionDenied" class="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6">
          <div class="fw-bold fs-6 text-gray-700">
            You do not have permission to view the cancellation exceptions.
          </div>
        </div>
        <template v-else>
          <div class="d-flex flex-wrap gap-3 mb-5" v-if="total > 0">
            <span
              v-for="option in categoryOptions"
              :key="'count-' + option.value"
              :class="['badge', 'py-3', 'px-4', 'fs-7', counts[option.value] ? option.badge : 'badge-light']"
            >
              {{ option.label }}: {{ counts[option.value] || 0 }}
            </span>
          </div>

          <table class="table align-middle table-row-dashed fs-6 gy-4 gs-4" v-loading="loading">
            <thead>
              <tr class="text-start text-gray-600 fw-bolder fs-7 text-uppercase gs-2">
                <th class="min-w-150px">What happened</th>
                <th class="min-w-100px">Order</th>
                <th class="min-w-125px">Customer</th>
                <th class="min-w-150px">Detail</th>
                <th class="min-w-100px">Requested by</th>
                <th class="min-w-125px">When</th>
                <th class="min-w-75px text-end">Order</th>
              </tr>
            </thead>
            <tbody class="fw-bold text-gray-600">
              <tr v-for="(item, index) in visibleItems" :key="'exception-' + index">
                <td>
                  <span :class="['badge', 'py-3', 'px-4', 'fs-7', categoryBadge(item.category)]">
                    {{ item.category_label }}
                  </span>
                  <div class="text-muted fs-7 mt-1" v-if="item.cancel_status_label">
                    {{ item.cancel_status_label }}
                  </div>
                </td>
                <td>
                  <span class="fw-bolder">{{ item.partner_order_id || item.order_id }}</span>
                  <div class="text-muted fs-7" v-if="item.trading_partner">{{ item.trading_partner }}</div>
                </td>
                <td>{{ item.customer_name || '—' }}</td>
                <td class="exception-note">
                  <div v-if="item.cancel_reason_label">{{ item.cancel_reason_label }}</div>
                  <div class="text-muted fs-7" v-if="item.cancel_reason">{{ item.cancel_reason }}</div>
                  <div class="text-muted fs-7" v-if="item.attempts > 0">Attempts: {{ item.attempts }}</div>
                </td>
                <td>
                  <span v-if="item.requested_by">{{ requestedByLabel(item.requested_by) }}</span>
                  <span v-else>—</span>
                  <div class="text-muted fs-7" v-if="item.source">via {{ sourceLabel(item.source) }}</div>
                </td>
                <td>
                  <div v-if="item.requested_at">Requested: {{ formatDateTime(item.requested_at) }}</div>
                  <div v-if="item.resolved_at">Resolved: {{ formatDateTime(item.resolved_at) }}</div>
                  <div class="text-muted fs-7" v-if="!item.requested_at && !item.resolved_at && item.at">
                    {{ formatDateTime(item.at) }}
                  </div>
                </td>
                <td class="text-end">
                  <el-tooltip v-if="item.order_id" class="box-item" effect="dark" content="Open the order" placement="top">
                    <button type="button" class="btn btn-icon btn-light btn-sm" @click="openOrder(item.order_id)">
                      <i class="bi bi-box-arrow-up-right"></i>
                    </button>
                  </el-tooltip>
                  <!-- A held-back status message can name an order HOFMP does not have. -->
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
              <!-- The server answers 200 with an error key when the list could
                   not be built. Saying "nothing needs attention" there would
                   report a broken query as an all-clear. -->
              <tr v-if="!loading && buildError">
                <td colspan="7" class="text-center text-danger py-10">
                  {{ buildError }} Nothing here can be trusted until it loads — try again, or check the logs.
                </td>
              </tr>
              <tr v-else-if="!loading && visibleItems.length === 0">
                <td colspan="7" class="text-center text-muted py-10">
                  Nothing needs attention in this window.
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, reactive, ref, onMounted } from "vue";
import ApiService from "@/core/services/ApiService";
import { useRouter } from "vue-router";
import { notificationFire } from "@/composable/notification.js";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { formatDateTime, formatText } from "@/core/helpers/common";
import { useRoute } from "vue-router";

/**
 * Operations list of cancellations that need a person: refused by the lab, no
 * outcome within the warning window, cancelled by the lab without a request, on
 * hold, and storefront updates that could not be delivered.
 *
 * Read-only: everything here is done from the order page.
 */
export default {
  name: "cancellation-exceptions",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const permissionDenied = ref(false);
    // Set when the server could not build the list but still answered 200.
    const buildError = ref("");
    const items = ref([]);
    const counts = ref({});
    const total = ref(0);

    const filterData = reactive({
      category: "",
      days: 30,
    });

    const dayOptions = [7, 14, 30, 60, 90];

    const categoryOptions = [
      { value: "rejected", label: "Cancellation refused by the lab", badge: "badge-light-danger" },
      { value: "no_outcome", label: "No outcome from the lab", badge: "badge-light-warning" },
      { value: "lab_initiated", label: "Cancelled by the lab", badge: "badge-light-warning" },
      { value: "on_hold", label: "On hold at the lab", badge: "badge-light-info" },
      { value: "storefront_failed", label: "Storefront not updated", badge: "badge-light-danger" },
      {
        value: "webhook_quarantined",
        label: "Status messages the lab sent that HOFMP could not verify",
        badge: "badge-light-danger",
      },
    ];

    const categoryBadge = (category) => {
      const option = categoryOptions.find((item) => item.value === category);
      return option ? option.badge : "badge-light-secondary";
    };

    const requestedByLabel = (who) => {
      const labels = { customer: "Customer", admin: "Operator", system: "System" };
      return labels[who] || who;
    };

    const sourceLabel = (source) => {
      const labels = { dg: "storefront", operator: "HOFMP", lab: "print lab", storefront: "storefront" };
      return labels[source] || source;
    };

    const visibleItems = computed(() => {
      if (!filterData.category) {
        return items.value;
      }
      return items.value.filter((item) => item.category === filterData.category);
    });

    const applyFilter = () => {
      // Filtering is local: the endpoint already returns the whole window.
    };

    const openOrder = (orderId) => {
      if (!orderId) {
        return;
      }
      const page = router.resolve({ name: "orderDetails", params: { id: orderId } });
      window.open(page.href, "_blank");
    };

    const getExceptions = async () => {
      loading.value = true;
      permissionDenied.value = false;
      buildError.value = "";
      await ApiService.query("cancellation-exceptions?days=" + filterData.days)
        .then(({ data }) => {
          const payload = data && data.data ? data.data : {};
          items.value = payload.items || [];
          counts.value = payload.counts || {};
          total.value = payload.total || 0;
          buildError.value = payload.error || "";
        })
        .catch((error) => {
          const status = error.response ? error.response.status : null;
          if (status === 403) {
            permissionDenied.value = true;
          } else {
            let message = "The cancellation exceptions could not be loaded.";
            if (error.response && error.response.data && error.response.data.message) {
              message = error.response.data.message;
            }
            notificationFire(message, "error");
          }
          items.value = [];
          counts.value = {};
          total.value = 0;
        })
        .finally(() => {
          loading.value = false;
        });
    };

    onMounted(async () => {
      setCurrentPageBreadcrumbs(formatText(route.name), [{ name: "Cancellation Exceptions", active: true }]);
      await getExceptions();
    });

    return {
      loading,
      permissionDenied,
      buildError,
      items,
      visibleItems,
      counts,
      total,
      filterData,
      dayOptions,
      categoryOptions,
      categoryBadge,
      requestedByLabel,
      sourceLabel,
      applyFilter,
      getExceptions,
      openOrder,
      formatDateTime,
    };
  },
};
</script>
